import Vue from 'vue';
import { convertPxToRem } from '@goodt-common/utils';
import { get as getByPath } from 'lodash';
import { CSS_VARS_DEFAULT_PREFIX } from './config';
import { $c } from './utils';

/**
 * Generates css-vars-style object
 *
 * @param {Record<string, string>} cssVars
 * @param {string} [prefix=CSS_VARS_DEFAULT_PREFIX]
 * @return {Record<string, string>}
 */
// prettier-ignore
export const buildCssVarsStyle = (cssVars, prefix = CSS_VARS_DEFAULT_PREFIX) =>
    Object.fromEntries(
        Object.entries(cssVars).map(
            ([varName, varValue]) => [
                `${prefix}-${varName}`,
                convertPxToRem(String(varValue))
            ])
    );
/**
 *
 * @param {string | string[] | function} mapping
 * @param {Record<string, any>} props
 * @return {*|undefined}
 */
const resolveCssVarsMapping = (mapping, props) => {
    if (typeof mapping === 'function') {
        return mapping(props);
    }
    if (typeof mapping === 'string') {
        return getByPath(props, mapping);
    }
    if (Array.isArray(mapping)) {
        const value = resolveCssVarsMapping(mapping[0], props);
        return value || mapping[1];
    }
    return mapping;
};

/**
 * Build css-vars name-value Map by mapping on props values
 *
 * @param {import('./types').CssVarsMapping} cssVarsMapping
 * @param {Record<string, any>} props
 * @return {Map<string, string | null>}
 */
export const buildCssVarsMap = (cssVarsMapping, props) => {
    const args = [cssVarsMapping, props];
    const invalidArg = args.find((arg) => arg == null || typeof arg !== 'object');
    if (invalidArg != null) {
        throw new Error(
            `Error during building css vars: ${
                args.indexOf(invalidArg) + 1
            } argument type or value is invalid: '${invalidArg}'`
        );
    }

    // prettier-ignore
    return new Map(
        Object.entries(cssVarsMapping)
            .map(([varName, mapping]) => {
                const mappedValue = resolveCssVarsMapping(mapping, props);
                const varValue = [undefined, null, ''].includes(mappedValue) ? null : String(mappedValue)
                return [varName, varValue];
            })
    )
};

/**
 * Build css-vars hash map object from css var to prop mapping
 *
 * @param {import('./types').CssVarsMapping} cssVarsMapping
 * @param {Record<string, any>} props
 * @return {Record<string, string>}
 */
export const buildCssVars = (cssVarsMapping, props) => {
    return Object.fromEntries(
        [...buildCssVarsMap(cssVarsMapping, props)]
            // filter nil value variables
            .filter(([, varValue]) => varValue != null)
            // apply string Constant processing
            .map(([varName, varValue]) => [varName, $c(varValue)])
    );
};
